import React from "react";

/**
 * Responsive image component that automatically serves optimized WebP
 * variants (desktop + mobile) with lazy loading.
 *
 * Usage:
 *   <SmartImg src="/media/quality.png" alt="..." className="..." priority />
 *
 * Expected file layout on public/:
 *   /media/quality.png        (original fallback)
 *   /media/quality.webp       (desktop-optimized)
 *   /media/quality-m.webp     (mobile-optimized, up to 800px wide)
 *
 * If a corresponding .webp does not exist (e.g. small logos not converted),
 * the browser falls back to the original <img src>.
 */
const OPTIMIZED_DIRS = ["/media/", "/products/", "/cocoa/", "/logos/", "/certs/"];

function derive(src) {
  if (!src || typeof src !== "string") return null;
  const isOptimized = OPTIMIZED_DIRS.some((d) => src.startsWith(d));
  if (!isOptimized) return null;
  const m = src.match(/^(.*)\.(png|jpe?g|webp)$/i);
  if (!m) return null;
  const base = m[1];
  return {
    desktop: `${base}.webp`,
    mobile: `${base}-m.webp`,
  };
}

export default function SmartImg({
  src,
  alt = "",
  className = "",
  priority = false,
  sizes,
  style,
  ...rest
}) {
  const variants = derive(src);
  const loading = priority ? "eager" : "lazy";
  const fetchPriority = priority ? "high" : "auto";

  if (!variants) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        style={style}
        {...rest}
      />
    );
  }

  return (
    <picture>
      <source
        media="(max-width: 768px)"
        srcSet={variants.mobile}
        type="image/webp"
      />
      <source srcSet={variants.desktop} type="image/webp" />
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        sizes={sizes}
        style={style}
        {...rest}
      />
    </picture>
  );
}
